import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSnackbar } from "notistack";
import axios from "../../config/axios";
import Loading from "components/Loading";
import {
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  TablePagination,
  Link as MuiLink,
} from "@material-ui/core";
import moment from "moment";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 25;

export default function SubscriptionSpaceHistory() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userIdFilter, setUserIdFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (userIdFilter.trim()) params.set("user_id", userIdFilter.trim());
      params.set("limit", String(rowsPerPage));
      params.set("skip", String(page * rowsPerPage));
      const res = await axios.get(`/admin/subscription-space-history?${params.toString()}`);
      setItems(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to fetch space history", {
        variant: "error",
      });
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [userIdFilter, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={2}>
      <Typography variant="h6" gutterBottom>
        {t("subscriptionSpaceHistory.title")}
      </Typography>
      <Box display="flex" alignItems="center" mb={2}>
        <TextField
          size="small"
          label={t("subscriptionSpaceHistory.userIdLabel")}
          value={userIdFilter}
          onChange={(e) => {
            setUserIdFilter(e.target.value);
            setPage(0);
          }}
          placeholder={t("subscriptionSpaceHistory.filterPlaceholder")}
          variant="outlined"
          style={{ marginRight: 8, minWidth: 220 }}
        />
        <Button variant="contained" color="primary" onClick={fetchHistory}>
          {t("subscriptionSpaceHistory.refresh")}
        </Button>
      </Box>
      {loading ? (
        <Loading />
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("subscriptionSpaceHistory.date")}</TableCell>
                <TableCell>{t("subscriptionSpaceHistory.eventType_")}</TableCell>
                <TableCell align="right">{t("subscriptionSpaceHistory.totalM2")}</TableCell>
                <TableCell align="right">{t("subscriptionSpaceHistory.boxes")}</TableCell>
                <TableCell>{t("subscriptionSpaceHistory.referenceId")}</TableCell>
                <TableCell>{t("subscriptionSpaceHistory.user")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {t("subscriptionSpaceHistory.noRecords")}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell>
                      {row.created_at
                        ? moment(row.created_at).format("YYYY-MM-DD HH:mm")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {row.event_type
                        ? t(`subscriptionSpaceHistory.eventType.${row.event_type}`, row.event_type)
                        : "—"}
                    </TableCell>
                    <TableCell align="right">{row.total_m2 ?? "—"}</TableCell>
                    <TableCell align="right">{row.boxes_count ?? "—"}</TableCell>
                    <TableCell>
                      {row.reference_id ? (
                        <MuiLink
                          component={Link}
                          to={
                            row.event_type === "return_completed"
                              ? `/admin/return-orders/${row.reference_id}`
                              : `/admin/orders/${row.reference_id}`
                          }
                        >
                          {String(row.reference_id)}
                        </MuiLink>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {row.user_id ? (
                        <MuiLink component={Link} to={`/admin/customers/${row.user_id}`}>
                          {row.user_name ? `${row.user_name} (${row.user_id})` : String(row.user_id)}
                        </MuiLink>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </Box>
  );
}
